import { FunctionComponent, useEffect, useState } from 'react';
import {
  Banner,
  Button,
  ButtonGroup,
  Icon,
  Link,
  SkeletonDisplayText,
  TextStyle,
  Tooltip,
} from '@shopify/polaris';
import {
  CancelSmallMinor,
  ClockMajor,
  PlusMinor,
  QuestionMarkMajor,
  StarOutlineMinor,
} from '@shopify/polaris-icons';
import { DropdownList, Select, Separator, SlidingToggle } from '@components/ui';
import Input from '@ui/input';
import {
  amplitudeEventQueueState,
  hubspotConversationsInitializedState,
  notificationErrorState,
  notificationSuccessState,
  selectedCampaignSelector,
} from '@states';
import { useRecoilState, useRecoilValue } from 'recoil';
import {
  CampaignStatus,
  ElementPosition,
  ElementRelativePosition,
  FeatureKey,
  FloaterRelativePosition,
} from '@givz/core-client';
import {
  useCoreClient,
  useFetchCampaigns,
  useFetchShouldPromptForReview,
} from '@hooks';
import styled, { css } from 'styled-components';
import { showHubspotChatWindow } from '@utils/instrumentation';
import {
  ExtendedRadioButtonProps,
  RadioStackProps,
} from '@components/ui/radio-stack';
import { MediaCard } from '@ui';

import {
  EllipsisTextContainer,
  FlexboxContainer,
  HeaderTitleBaseStyles,
} from '@ui/common';
import { Routes } from '@routes';
import { useRouter } from 'next/router';
import { openUrlInNewTab } from '@utils/url';
import { Sectioned } from '@components/ui/container';
import { ToggleProps } from '@components/ui/sliding-toggle';
import { openReviewUrlInNewTab } from '@hooks/account';
import { CampaignWithReadOnly } from 'src/types';
import { useSubscriptionFeatures } from '@hooks/subscription';
import { cloneDeep } from 'lodash';
import { useCoreClientSafe } from '@utils/core-client';

const floaterPositionOptions: ExtendedRadioButtonProps[] = [
  {
    id: FloaterRelativePosition.TopLeft,
    label: 'Top - left',
    name: FloaterRelativePosition.TopLeft,
  },
  {
    id: FloaterRelativePosition.TopRight,
    label: 'Top - right',
    name: FloaterRelativePosition.TopRight,
  },
  {
    id: FloaterRelativePosition.MiddleLeft,
    label: 'Middle - left',
    name: FloaterRelativePosition.MiddleLeft,
  },
  {
    id: FloaterRelativePosition.MiddleRight,
    label: 'Middle - right',
    name: FloaterRelativePosition.MiddleRight,
  },
  {
    id: FloaterRelativePosition.BottomLeft,
    label: 'Bottom - left',
    name: FloaterRelativePosition.BottomLeft,
  },
  {
    id: FloaterRelativePosition.BottomRight,
    label: 'Bottom - right',
    name: FloaterRelativePosition.BottomRight,
  },
  {
    id: FloaterRelativePosition.BottomCenter,
    label: 'Bottom - middle',
    name: FloaterRelativePosition.BottomCenter,
  },
];

const modalIntervalOptions: ExtendedRadioButtonProps[] = [
  {
    id: '7',
    label: 'Once per week',
    name: '7',
  },
  {
    id: '30',
    label: 'Once per month',
    name: '30',
  },
  {
    id: '365',
    label: 'Once per year',
    name: '365',
  },
];

const widgetPositionOptions = [
  {
    label: 'Above',
    value: ElementRelativePosition.Above,
  },
  {
    label: 'Below',
    value: ElementRelativePosition.Below,
  },
  {
    label: 'First Child',
    value: ElementRelativePosition.FirstChild,
  },
  {
    label: 'Last Child',
    value: ElementRelativePosition.LastChild,
  },
];

export const FloaterPositionSelect: FunctionComponent<
  Pick<RadioStackProps, 'onToggle' | 'initialState'>
> = (props) => {
  const { initialState } = props;

  const getText = (currentValue: string) => {
    const floaterPosition = floaterPositionOptions.find(
      (floaterPositionOption) => floaterPositionOption.id === currentValue,
    );

    return floaterPosition?.label ?? '';
  };

  const [text, setText] = useState(getText(initialState));

  useEffect(() => {
    setText(getText(initialState));
  }, [initialState]);

  return (
    <DropdownList
      activator={<TextStyle>{text}</TextStyle>}
      radioStackProps={{ buttons: floaterPositionOptions, ...props }}
    />
  );
};

export const HowGivzWorksMediaCardTitle = 'How to get started';

export const HowGivzWorksMediaCard: FunctionComponent = () => {
  const [amplitudeEventQueue, setAmplitudeEventQueue] = useRecoilState(
    amplitudeEventQueueState,
  );

  return (
    <MediaCard
      title={HowGivzWorksMediaCardTitle}
      description="You can be up and running in less than 5 minutes."
      mediaSrc="https://cdn.loom.com/sessions/thumbnails/de22701b3f454b70bb084da32b4ae3d0-1644416428799-with-play.gif"
      onMediaClick={() => {
        openUrlInNewTab(
          'https://www.loom.com/share/de22701b3f454b70bb084da32b4ae3d0',
        );
        setAmplitudeEventQueue([
          ...amplitudeEventQueue,
          { name: 'viewed getting started video' },
        ]);
      }}
      portrait={true}
    />
  );
};

export const ModalIntervalSelect: FunctionComponent<
  Pick<RadioStackProps, 'disabled' | 'onToggle' | 'initialState'>
> = (props) => {
  const { initialState } = props;

  const getText = (currentValue: string) => {
    const modalInterval = modalIntervalOptions.find(
      (modalIntervalOption) => modalIntervalOption.id === currentValue,
    );

    return modalInterval?.label ?? '';
  };

  const [text, setText] = useState(getText(initialState));

  useEffect(() => {
    setText(getText(initialState));
  }, [initialState]);

  return (
    <DropdownList
      activator={<TextStyle>{text}</TextStyle>}
      radioStackProps={{ buttons: modalIntervalOptions, ...props }}
    />
  );
};

const SupportEmailLink = styled.div`
  margin-top: 0.7rem;
  .Polaris-Link--monochrome {
    color: ${({ theme }) => theme.colors.font.primary.hex};
    font-weight: 400;
  }
`;

const SupportBannerChatButton = styled.div`
  margin-top: 0.7rem;
`;

export const SupportBanner: FunctionComponent = () => {
  const hubspotConversationsInitialized = useRecoilValue(
    hubspotConversationsInitializedState,
  );

  return (
    <Banner title="Have any questions?" icon={QuestionMarkMajor}>
      <SupportEmailLink>
        <a target="_blank" href="mailto:support@givz.com" rel="noreferrer">
          Email us
        </a>
      </SupportEmailLink>
      {hubspotConversationsInitialized && (
        <SupportBannerChatButton>
          <Link onClick={() => showHubspotChatWindow()}>Chat now</Link>
        </SupportBannerChatButton>
      )}
    </Banner>
  );
};

const ReviewSidebarBannerContainer = styled.div`
  margin: 2rem 0 2rem 0;

  .Polaris-Banner__Heading {
    margin-bottom: 1rem;
  }

  .Polaris-Banner__Actions {
    padding-top: 1rem;
  }
`;

export const ReviewSidebarBanner: FunctionComponent = () => {
  const { showReviewSidebarPrompt } = useFetchShouldPromptForReview();

  if (!showReviewSidebarPrompt) return null;

  return (
    <ReviewSidebarBannerContainer>
      <Banner
        icon={StarOutlineMinor}
        title="We have a favor to ask"
        status="info"
        action={{
          content: 'Leave a Review',
          onAction: openReviewUrlInNewTab,
        }}
      >
        <TextStyle>
          Givz is on a mission to give $1B, but we need your help.
          <br />
          <br />
          Leaving an app store review helps us reach more merchants like
          yourselves.
        </TextStyle>
      </Banner>
    </ReviewSidebarBannerContainer>
  );
};

export const SupportReviewSidebarBanners: FunctionComponent = () => {
  return (
    <>
      <SupportBanner />
      <ReviewSidebarBanner />
    </>
  );
};

const CampaignNameContainer = styled(FlexboxContainer)`
  justify-content: space-between;
  margin-top: 0.5rem;

  @media (max-width: 768px) {
    flex-direction: column;
    justify-content: flex-start;
  }
`;

const CampaignNameContentContainer = styled(FlexboxContainer)`
  align-items: center;
  display: flex;
  min-height: 40px;
  width: 100%;

  @media (max-width: 768px) {
    margin-bottom: 1.5rem;
  }
`;

const StyledPageTitle = styled(EllipsisTextContainer)`
  ${HeaderTitleBaseStyles};
  margin: 0.25rem 1rem 0 0;
  max-width: 565px;
`;

const NameEditInputContainer = styled.div`
  width: 100%;
  .Polaris-TextField__Input {
    min-width: 565px;
    font-size: 20px;
    font-weight: 500;

    @media (max-width: 768px) {
      min-width: 100px;
    }
  }

  .Polaris-TextField__Backdrop {
    background-color: transparent;
  }

  .Polaris-ButtonGroup {
    justify-content: flex-end;
    margin-top: 0.5rem;
  }
`;

export const PageTitleContent: FunctionComponent = () => {
  const coreClient = useCoreClient();
  const coreClientSafe = useCoreClientSafe();
  const selectedCampaign = useRecoilValue(selectedCampaignSelector);
  const { setCampaigns } = useFetchCampaigns();
  const [amplitudeEventQueue, setAmplitudeEventQueue] = useRecoilState(
    amplitudeEventQueueState,
  );
  const { isFeatureReadOnly } = useSubscriptionFeatures();
  const [, setNotificationSuccess] = useRecoilState(notificationSuccessState);
  const [notificationError, setNotificationError] = useRecoilState(
    notificationErrorState,
  );
  const [loading, setLoading] = useState(false);
  const router = useRouter();
  const [status, setStatus] = useState<CampaignStatus | undefined>(
    selectedCampaign?.status,
  );
  const [editCampaignNameLoading, setEditCampaignNameLoading] = useState(false);
  const [editCampaignNameOpen, setEditCampaignNameOpen] = useState(false);
  const [campaignName, setCampaignName] = useState(
    selectedCampaign?.name ?? '',
  );

  useEffect(() => {
    setStatus(selectedCampaign?.status);
    setCampaignName(selectedCampaign?.name ?? '');
  }, [selectedCampaign]);

  const onToggleCampaignStatus = async (isActive: boolean) => {
    setLoading(true);
    if (notificationError) setNotificationError(undefined);

    const defaultErrorMessage = `Incentive not ${
      isActive ? 'activated' : 'deactivated'
    }`;

    if (!selectedCampaign) {
      setNotificationError(defaultErrorMessage);
      setLoading(false);
      return;
    }

    const { data, errors } = await coreClientSafe({
      args: {
        body: {},
        gid: selectedCampaign.gid,
      },
      fn: isActive
        ? coreClient().Campaigns.activate
        : coreClient().Campaigns.deactivate,
    });

    if (errors) {
      setNotificationError(errors);
      setStatus(selectedCampaign.status);
    } else {
      setCampaigns(data.campaigns);

      setNotificationSuccess(
        `Incentive is now ${isActive ? 'ACTIVE' : 'INACTIVE'}`,
      );
      setAmplitudeEventQueue([
        ...amplitudeEventQueue,
        {
          name: 'saved campaign status',
          params: {
            activate: status === CampaignStatus.Active ? 'on' : 'off',
          },
        },
      ]);
    }

    setLoading(false);
  };

  const onSaveCampaignName = async () => {
    setEditCampaignNameLoading(true);
    if (notificationError) setNotificationError(undefined);

    const defaultErrorMessage = 'Incentive name not saved';

    if (!selectedCampaign) {
      setNotificationError(defaultErrorMessage);
      setEditCampaignNameLoading(false);
      return;
    }

    const { data, errors } = await coreClientSafe({
      args: {
        campaignUpdate: {
          name: campaignName,
        },
        gid: selectedCampaign.gid,
      },
      fn: coreClient().Campaigns.update,
    });

    if (errors) {
      setNotificationError(errors);
    } else {
      setCampaigns(data.campaigns);
      setEditCampaignNameOpen(false);

      setNotificationSuccess(`Incentive name saved successfully`);
      setAmplitudeEventQueue([
        ...amplitudeEventQueue,
        {
          name: 'saved campaign name',
          params: {
            name: campaignName,
          },
        },
      ]);
    }

    setEditCampaignNameLoading(false);
  };

  const onToggleEditCampaignName = () => {
    setEditCampaignNameOpen(!editCampaignNameOpen);

    if (editCampaignNameOpen) {
      setCampaignName(selectedCampaign?.name ?? '');
    }
  };

  const onChangeInput = (value: string) => setCampaignName(value);

  return (
    <Sectioned secondary={<></>}>
      <div>
        <span>
          <Link
            onClick={() => router.push({ pathname: Routes.incentives.list })}
          >
            Incentives
          </Link>
        </span>
        <span>{' > #' + selectedCampaign?.gid}</span>
      </div>

      <CampaignNameContainer>
        {!selectedCampaign && <SkeletonDisplayText />}
        {selectedCampaign && (
          <CampaignNameContentContainer>
            {!editCampaignNameOpen && (
              <StyledPageTitle>{selectedCampaign.name}</StyledPageTitle>
            )}
            {editCampaignNameOpen && (
              <NameEditInputContainer>
                <Input
                  label=""
                  labelHidden={true}
                  id="campaignName"
                  onChange={onChangeInput}
                  value={campaignName}
                  placeholder="Enter a campaign name"
                  minLength={3}
                  multiline={1}
                />
                <ButtonGroup>
                  <Button onClick={onToggleEditCampaignName}>Cancel</Button>
                  <Button
                    primary={true}
                    onClick={onSaveCampaignName}
                    loading={editCampaignNameLoading}
                  >
                    Save
                  </Button>
                </ButtonGroup>
              </NameEditInputContainer>
            )}
            {!editCampaignNameOpen &&
              !isFeatureReadOnly(FeatureKey.CoreBenefits, selectedCampaign) && (
                <Link onClick={onToggleEditCampaignName}>Rename</Link>
              )}
          </CampaignNameContentContainer>
        )}
        {!editCampaignNameOpen && selectedCampaign && status !== undefined && (
          <IncentiveToggle
            selectedCampaign={selectedCampaign}
            id="campaignStatusToggle"
            onToggle={onToggleCampaignStatus}
            loading={loading}
          />
        )}
      </CampaignNameContainer>
    </Sectioned>
  );
};

type IncentiveToggleProps = {
  selectedCampaign: CampaignWithReadOnly;
} & Pick<ToggleProps, 'id' | 'loading' | 'onToggle' | 'className'>;

const IncentiveToggleStyled = styled(
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  ({ tertiary, ...toggleProps }: ToggleProps & { tertiary?: boolean }) => (
    <SlidingToggle {...toggleProps} />
  ),
)`
  ${({ tertiary }) =>
    tertiary &&
    css`
      .react-toggle--checked .react-toggle-track,
      .react-toggle--checked .react-toggle-track:hover {
        background-color: ${({ theme }) =>
          theme.colors.secondary.hex} !important;
      }

      .Polaris-Icon--colorBase svg {
        fill: ${({ theme }) => theme.colors.secondary.hex};
      }
    `}
`;

export const IncentiveToggle: FunctionComponent<IncentiveToggleProps> = ({
  selectedCampaign,
  ...toggleProps
}) => {
  const { isFeatureReadOnly } = useSubscriptionFeatures();

  return (
    <IncentiveToggleStyled
      tertiary={CampaignStatus.Scheduled === selectedCampaign.status}
      activeText={
        CampaignStatus.Active === selectedCampaign.status ? (
          'On'
        ) : (
          <Tooltip content="Scheduled" dismissOnMouseOut>
            <Icon source={ClockMajor} color="base" />
          </Tooltip>
        )
      }
      disabled={
        selectedCampaign.status === CampaignStatus.Archived ||
        isFeatureReadOnly(FeatureKey.CoreBenefits, selectedCampaign)
      }
      inActiveText={
        selectedCampaign.status === CampaignStatus.Archived ? 'Archived' : 'Off'
      }
      isActive={[CampaignStatus.Active, CampaignStatus.Scheduled].includes(
        selectedCampaign.status,
      )}
      {...toggleProps}
    />
  );
};

const PositionSettingsContainer = styled(FlexboxContainer)`
  justify-content: space-between;
  align-items: center;

  .Polaris-Button {
    align-self: center;
    height: 100%;
  }
`;

const PositionSelectorInputContainer = styled.div`
  width: 65%;

  @media (max-width: 768px) {
    width: 50%;
  }
`;

const PositionRelativeDropdownContainer = styled.div`
  width: 20%;

  @media (max-width: 768px) {
    width: 30%;
  }
`;

export const ExcludedRoutesSelector: FunctionComponent<{
  disabled?: boolean;
  onChange(updatedRoutes: string[]): void;
  routes: string[];
  subTitle?: React.ReactNode;
  title?: React.ReactNode;
}> = (props) => {
  const { disabled, onChange, routes, subTitle, title } = props;

  const [elementRoutes, setElementRoutes] = useState(routes);

  useEffect(() => {
    setElementRoutes(routes);
  }, [routes]);

  const onChangeSetting = (index: number, newValue: any) => {
    const updatedRoutes = cloneDeep(routes);
    updatedRoutes[index] = newValue;

    setElementRoutes(updatedRoutes);

    onChange(updatedRoutes);
  };

  const onAddRoute = () => {
    const updatedRoutes = [...elementRoutes, ''];

    setElementRoutes(updatedRoutes);
    onChange(updatedRoutes);
  };

  const onRemoveRoute = (index: number) => {
    const updatedRoutes = cloneDeep(elementRoutes);

    updatedRoutes.splice(index, 1);
    setElementRoutes(updatedRoutes);
    onChange(updatedRoutes);
  };

  return (
    <>
      {title && title}
      {subTitle && subTitle}
      {elementRoutes.map((route, index) => (
        <div key={index}>
          <PositionSettingsContainer>
            <PositionSelectorInputContainer>
              <Input
                label=""
                labelHidden
                onChange={(value: string, _id: string) =>
                  onChangeSetting(index, value)
                }
                value={route}
                disabled={disabled}
                placeholder="/cart"
              />
            </PositionSelectorInputContainer>
            <Button
              icon={CancelSmallMinor}
              onClick={() => onRemoveRoute(index)}
            />
          </PositionSettingsContainer>
          <Separator />
        </div>
      ))}
      <Button icon={PlusMinor} onClick={onAddRoute}>
        Add New
      </Button>
    </>
  );
};

export const PositionSelector: FunctionComponent<{
  disabled?: boolean;
  onChange(updatedPositions: ElementPosition[]): void;
  positions: ElementPosition[];
  subTitle?: React.ReactNode;
  title?: React.ReactNode;
}> = (props) => {
  const { disabled, onChange, positions, subTitle, title } = props;

  const [elementPositions, setElementPositions] = useState(positions);

  useEffect(() => {
    setElementPositions(positions);
  }, [positions]);

  const onChangeSetting = (
    index: number,
    key: keyof ElementPosition,
    newValue: any,
  ) => {
    const updatedPositions = cloneDeep(positions);
    updatedPositions[index][key] = newValue;

    setElementPositions(updatedPositions);

    onChange(updatedPositions);
  };

  const onChangeCssSelector = (index: number, newValue: string) =>
    onChangeSetting(index, 'selector', newValue);

  const onToggleRelativePosition = (index: number, newValue: string) =>
    onChangeSetting(index, 'relative', newValue);

  const onAddPosition = () => {
    const updatedPositions = [
      ...elementPositions,
      {
        relative: ElementRelativePosition.FirstChild,
        selector: '',
      },
    ];

    setElementPositions(updatedPositions);
    onChange(updatedPositions);
  };

  const onRemovePosition = (index: number) => {
    const updatedPositions = cloneDeep(elementPositions);

    updatedPositions.splice(index, 1);
    setElementPositions(updatedPositions);
    onChange(updatedPositions);
  };

  return (
    <>
      {title && title}
      {subTitle && subTitle}
      {elementPositions.map((position, index) => (
        <div key={index}>
          <PositionSettingsContainer>
            <PositionSelectorInputContainer>
              <Input
                label=""
                labelHidden
                onChange={(value: string, _id: string) =>
                  onChangeCssSelector(index, value)
                }
                value={position.selector}
                disabled={disabled}
                placeholder="CSS Selector"
              />
            </PositionSelectorInputContainer>
            <PositionRelativeDropdownContainer>
              <Select
                label=""
                labelInline={false}
                options={widgetPositionOptions}
                onChange={(value: string, _: string) =>
                  onToggleRelativePosition(index, value)
                }
                value={position.relative}
                disabled={disabled}
              />
            </PositionRelativeDropdownContainer>
            <Button
              icon={CancelSmallMinor}
              onClick={() => onRemovePosition(index)}
              disabled={positions.length === 1}
            />
          </PositionSettingsContainer>

          <Separator />
        </div>
      ))}
      <Button icon={PlusMinor} onClick={onAddPosition}>
        Add New Selector
      </Button>
    </>
  );
};
